import React from 'react';

const NOT_DETECTED = 'Not detected';
const NOT_FOUND = 'Not found';

const blank = (value) => {
  if (value == null) return true;
  const s = String(value).trim();
  if (!s) return true;
  const lower = s.toLowerCase();
  return lower === 'unknown' || lower === 'n/a' || lower === 'null' || lower === '-';
};

const text = (value) => (blank(value) ? '' : String(value).trim());

const parse = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return null;
};

const hasKey = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const countOf = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatCount = (num) => {
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(num);
};

const titleCase = (value) => {
  const s = text(value);
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const scannedLabel = (value) => {
  const t = Date.parse(value || '');
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const hostOf = (url) => String(url || '')
  .replace(/^https?:\/\//i, '')
  .replace(/^www\./i, '')
  .replace(/\/+$/, '');

const skippedNote = (parts) => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return `${parts[0]} was not part of this scan.`;
  return `${parts[0]} and ${parts[1]} were not part of this scan.`;
};

function Fact({ icon, label, value, empty }) {
  return (
    <div className="ir-wa-row">
      <span className="ir-wa-row-label"><i className={`fa ${icon}`} />{label}</span>
      <span className={`ir-wa-row-value${empty ? ' ir-wa-row-value--empty' : ''}`}>{value}</span>
    </div>
  );
}

/** Technical website scan for the Intelligence view (data: contact.websiteAnalysis). */
export default function WebsiteAnalyzerInsight({ analysis, scannedAt }) {
  const wa = parse(analysis);
  if (!wa) return null;

  const modules = new Set(
    Array.isArray(wa.activeModules) && wa.activeModules.length ? wa.activeModules : ['webSystem'],
  );
  const covered = (moduleId, keys) => modules.has(moduleId) || keys.some((key) => wa[key] !== undefined);

  const age = (wa.websiteAge && typeof wa.websiteAge === 'object') ? wa.websiteAge
    : ((wa.website_age && typeof wa.website_age === 'object') ? wa.website_age : null);
  const ageLabel = titleCase(age?.label);
  const ageEra = text(age?.estimatedEra || age?.estimated_era);
  const ageScore = typeof age?.score === 'number' ? age.score : null;
  const findings = (Array.isArray(age?.top_findings) ? age.top_findings : (Array.isArray(age?.topFindings) ? age.topFindings : []))
    .map((item) => text(item))
    .filter(Boolean)
    .slice(0, 6);
  const ageKnown = Boolean(ageLabel || ageScore != null || ageEra || findings.length);

  const cms = text(wa.cms);
  const confidence = typeof wa.cms_confidence === 'number'
    ? wa.cms_confidence
    : (typeof wa.cmsConfidence === 'number' ? wa.cmsConfidence : null);
  const versionEstimate = text(wa.version_estimate || wa.versionEstimate);
  const shopwareMajor = text(wa.shopware_major_version || wa.shopwareMajorVersion);
  const version = versionEstimate && shopwareMajor && !versionEstimate.startsWith(shopwareMajor)
    ? `${versionEstimate} · major ${shopwareMajor}`
    : (versionEstimate || shopwareMajor);
  const editionRaw = text(wa.edition_estimate || wa.editionEstimate);
  const isShopware = wa.is_shopware === true || wa.isShopware === true;
  const isShop = wa.is_shop === true || wa.isShop === true;
  const shopKnown = isShopware || isShop
    || hasKey(wa, 'is_shop') || hasKey(wa, 'isShop')
    || hasKey(wa, 'is_shopware') || hasKey(wa, 'isShopware');
  const edition = (!cms && !isShopware && /^community\b/i.test(editionRaw) && /open source/i.test(editionRaw))
    ? ''
    : editionRaw;
  const tech = titleCase(wa.tech_stack_maturity || wa.techStackMaturity);
  const industry = text(wa.industry);
  const company = text(wa.company_name || wa.companyName);
  const city = text(wa.city);
  const angle = text(wa.recommended_sales_angle || wa.recommendedSalesAngle);
  const siteName = text(wa.site_name || wa.siteName);
  const siteNameKnown = hasKey(wa, 'site_name') || hasKey(wa, 'siteName');
  const pluginsKnown = hasKey(wa, 'plugins_total') || hasKey(wa, 'pluginsTotal');
  const plugins = countOf(wa.plugins_total ?? wa.pluginsTotal);
  const products = countOf(wa.product_count ?? wa.productCount);
  const categories = countOf(wa.category_count ?? wa.categoryCount);
  const showProducts = covered('productCount', ['product_count', 'productCount', 'category_count', 'categoryCount']);
  const showAge = covered('websiteAge', ['website_age', 'websiteAge']);
  const when = scannedLabel(wa.analyzedAt || wa.analyzed_at || scannedAt);
  const host = hostOf(wa.url);

  const shopValue = isShopware ? 'Shopware' : (isShop ? 'Yes' : 'Not a shop');

  const metrics = [
    cms && {
      label: 'CMS',
      value: cms,
      hint: confidence != null && confidence > 0 ? `${Math.round(confidence * 100)}%` : '',
    },
    showProducts && products != null && { label: 'Products', value: formatCount(products) },
    showProducts && categories != null && { label: 'Categories', value: formatCount(categories) },
    showAge && ageKnown && (ageScore != null || ageLabel || ageEra) && {
      label: 'Website age',
      value: ageScore != null ? String(ageScore) : (ageLabel || ageEra),
      hint: [ageScore != null ? ageLabel : '', ageScore != null || ageLabel ? ageEra : ''].filter(Boolean).join(' · '),
      tone: ageScore == null ? '' : (ageScore >= 70 ? 'good' : ageScore >= 40 ? 'mid' : 'low'),
    },
  ].filter((item) => item && item.value);

  const rows = [
    !cms && { icon: 'fa-server', label: 'CMS', value: NOT_DETECTED, empty: true },
    { icon: 'fa-code-branch', label: 'Version', value: version || NOT_DETECTED, empty: !version },
    { icon: 'fa-store', label: 'Shop', value: shopKnown ? shopValue : NOT_DETECTED, empty: !shopKnown },
    { icon: 'fa-layer-group', label: 'Edition', value: edition || NOT_DETECTED, empty: !edition },
    { icon: 'fa-server', label: 'Tech stack', value: tech || NOT_DETECTED, empty: !tech },
    siteNameKnown && { icon: 'fa-globe', label: 'Site name', value: siteName || NOT_FOUND, empty: !siteName },
    pluginsKnown && {
      icon: 'fa-plug',
      label: 'Plugins',
      value: plugins == null ? NOT_DETECTED : formatCount(plugins),
      empty: plugins == null,
    },
    { icon: 'fa-industry', label: 'Industry', value: industry || NOT_FOUND, empty: !industry },
    { icon: 'fa-building', label: 'Company', value: company || NOT_FOUND, empty: !company },
    { icon: 'fa-location-dot', label: 'City', value: city || NOT_FOUND, empty: !city },
    showProducts && products == null && { icon: 'fa-box-open', label: 'Products', value: NOT_DETECTED, empty: true },
    showProducts && categories == null && { icon: 'fa-folder-tree', label: 'Categories', value: NOT_DETECTED, empty: true },
    showAge && !ageKnown && { icon: 'fa-hourglass-half', label: 'Website age', value: NOT_DETECTED, empty: true },
  ].filter(Boolean);

  const skipped = skippedNote([
    !showProducts && 'Product count',
    !showAge && 'Website age',
  ].filter(Boolean));

  const scannedBits = [host, when].filter(Boolean);

  return (
    <section className="ir-section ir-wa">
      <div className="ir-section-label">
        <i className="fa fa-laptop-code" />Website Analyzer
      </div>
      <div className="ir-wa-body">
        {metrics.length > 0 && (
          <div className="ir-wa-grid">
            {metrics.map((metric) => (
              <div key={metric.label} className="ir-wa-metric">
                <span className="ir-wa-label">{metric.label}</span>
                <span className={`ir-wa-value${metric.tone ? ` ir-wa-value--${metric.tone}` : ''}`}>{metric.value}</span>
                {metric.hint && <span className="ir-wa-hint">{metric.hint}</span>}
              </div>
            ))}
          </div>
        )}
        {rows.map((row) => <Fact key={row.label} {...row} />)}
        {findings.length > 0 && (
          <ul className="ir-wa-findings">
            {findings.map((finding) => <li key={finding}>{finding}</li>)}
          </ul>
        )}
        {angle ? (
          <p className="ir-wa-angle"><i className="fa fa-bullseye" />{angle}</p>
        ) : (
          <Fact icon="fa-bullseye" label="Sales angle" value={NOT_FOUND} empty />
        )}
        {skipped && <p className="ir-wa-note">{skipped}</p>}
      </div>
      {scannedBits.length > 0 && (
        <div className="ir-seo-footer">
          <i className="fa fa-clock" />
          Scanned {scannedBits.join(' · ')}
        </div>
      )}
    </section>
  );
}
